import { isEqual } from "lodash";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import { Error500 } from "@/components/errors/Error500";
import type { Modality } from "@/components/graphql/schema.graphql.d";
import { fetchWithRetry } from "@/utils/fetch-retry";

import { type LayerConfig } from "../../config";
import { ConfigContext } from "./useConfig";

export { useConfig } from "./useConfig";

// Pricing endpoint returns lowercase modality, normalize to match GraphQL Modality enum
// FIXME: should be fixed in the backend, but pricing.json is consumed by external clients (Framer)
const normalizeConfig = (config: LayerConfig): LayerConfig => ({
  ...config,
  PRICING: {
    ...config.PRICING,
    model_price_list: config.PRICING.model_price_list.map((p) => ({
      ...p,
      modality: p.modality.toUpperCase() as Modality,
    })),
  },
});

export const ConfigProvider = (props: PropsWithChildren) => {
  const { children } = props;

  const [config, setConfig] = useLocalStorage<LayerConfig | null>(
    "config",
    null,
  );
  const configRef = useRef<LayerConfig | null>(
    config ? normalizeConfig(config) : null,
  ); // Note: useLocalStorage cause an extra re-render, even when the value is the same, so we use a ref to avoid it

  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetchWithRetry("/backend/config/web.json", {
          signal: controller.signal,
        });
        const rawConfig = (await response.json()) as LayerConfig;

        if (controller.signal.aborted) return;

        const newConfig = normalizeConfig(rawConfig);

        // Only update if the config has actually changed, avoid unnecessary re-renders
        if (!isEqual(configRef.current, newConfig)) {
          setConfig(newConfig);
          configRef.current = newConfig;
        }
        setError(false);
      } catch {
        if (controller.signal.aborted) return;
        setError(true);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [setConfig]);

  if (!configRef.current) {
    if (error) {
      return <Error500 showHelpLinks />;
    }
    return null; // wait for config to be loaded
  }

  return (
    <ConfigContext.Provider value={configRef.current}>
      {children}
    </ConfigContext.Provider>
  );
};
